import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { useCaseOverview } from "@/api";
import { Bone } from "@/components/Bone";
import { CaseCustomer, caseLineClass, CaseStage, CaseVolume, CaseWhat, CaseWhen } from "@/components/CaseCells";
import { Figures } from "@/components/Figures";
import { OPEN_STATUSES } from "@/lib/stages";

import styles from "./RecentCases.module.css";

export const RECENT_LIMIT = 5;

// No direction in it (UX gate round 1, #1): the two ways in stand under the
// list now, and the start page holds the list back on a first visit anyway —
// this is the rare list that answers empty while mine does not.
const EMPTY = "Noch kein Vorgang — lies eine Zeichnung ein oder zeichne einen Umriss.";
const UNREACHABLE = "Vorgänge nicht erreichbar — Neuanfangen geht trotzdem.";

/** A stage chip's own box: an 11 px line at 1.5, plus 1 px padding and 1 px border above and below. */
const CHIP_HEIGHT = "calc(var(--text-xs) * 1.5 + 4px)";

/**
 * The skeleton in the rows' own cells and line boxes (UX ruling 11, F-5):
 * with the same cell classes it takes the same place as a finished row in
 * every tier — table row or card — and a bone in a line box is exactly a
 * line of text tall.
 */
/*
 * Widths in rem, not percent: on a card the title and its number, the chip
 * and its word stand side by side, sized by their content — a percentage of
 * that is nothing.
 */
const SKELETON: readonly { cell: string; lines: readonly string[] }[] = [
  { cell: styles.cellCase ?? "", lines: ["10rem", "4.5rem"] },
  { cell: styles.cellCustomer ?? "", lines: ["7rem"] },
  { cell: styles.cellStage ?? "", lines: ["chip", "5.5rem"] },
  { cell: styles.cellVolume ?? "", lines: ["4.5rem"] },
  { cell: styles.cellWhen ?? "", lines: ["5rem", "6rem"] },
];

function Skeleton() {
  return (
    <ul className={`${styles.rows} ${styles.skeletonRows}`} aria-busy="true" aria-label="Vorgänge werden geladen">
      {Array.from({ length: RECENT_LIMIT }, (_, row) => (
        <li key={row} className={styles.skeleton}>
          {SKELETON.map(({ cell, lines }, index) => (
            <span key={index} className={`${styles.cell} ${cell}`}>
              {lines.map((width, line) => (
                <span
                  key={line}
                  className={`${line === 0 ? caseLineClass.one : caseLineClass.two} ${width === "chip" ? styles.boneChip : styles.boneLine}`}
                >
                  <Bone
                    width={width === "chip" ? "5.5rem" : width}
                    height={width === "chip" ? CHIP_HEIGHT : line === 0 ? "0.75rem" : "0.625rem"}
                  />
                </span>
              ))}
            </span>
          ))}
        </li>
      ))}
    </ul>
  );
}

/**
 * „Zuletzt bearbeitet" (design 2026-09-22 §8.4, D-8): exactly five rows,
 * opening is the only thing a row does, and the register is one link away.
 * The stage gestures that stood here moved to the register and the record —
 * the start page stays quiet.
 *
 * A row is the register's row (UX gate round 2, #1): the same cells from
 * `CaseCells`, in the same order, the same panel, the same 64 px and the
 * same column widths for the stage, the amount and the moment — only
 * without the email line, the step and a head. Without a head, a missing
 * amount says so in words, never a bare dash (R2-12). One grid on the list,
 * every row and its link a subgrid of it, so every column keeps one edge.
 */
export function RecentCases() {
  // Asked first, like the register's own open count (RegisterScreen, fix
  // round 1, I3): the default answer keeps `won`/`lost` (it only drops the
  // three finished statuses), so „N offenen" is asked for separately, over
  // `OPEN_STATUSES` — a decided Vorgang is not „offen".
  const openOverview = useCaseOverview({ status: [...OPEN_STATUSES], limit: 1 });
  const overview = useCaseOverview({ sort: "activity", limit: RECENT_LIMIT });
  const page = overview.data;

  let content: ReactNode;
  if (overview.isPending && page === undefined) {
    content = (
      <div className={styles.frame}>
        <Skeleton />
        {/* The foot's line too, or everything under the list moves down by it on arrival. */}
        <p className={styles.more} aria-hidden="true">
          <span className={`${caseLineClass.one} ${styles.boneLine}`}>
            <Bone width="7rem" height="0.75rem" />
          </span>
        </p>
      </div>
    );
  } else if (page === undefined) {
    content = <p className={styles.note}>{UNREACHABLE}</p>;
  } else if (page.items.length === 0) {
    content = <p className={styles.note}>{EMPTY}</p>;
  } else {
    const shown = page.items.slice(0, RECENT_LIMIT);
    // While the open count has not answered yet, the main page's own total
    // is the nearest honest stand-in — the two agree whenever nothing shown
    // is won or lost.
    const openCount = openOverview.data?.totalCount ?? page.totalCount;
    content = (
      <div className={styles.frame}>
        <ul className={styles.rows}>
          {shown.map((item) => (
            <li key={item.id}>
              <Link className={styles.row} to={`/vorgang/${item.id}`}>
                <span className={`${styles.cell} ${styles.cellCase}`}>
                  <CaseWhat item={item} />
                </span>
                <span className={`${styles.cell} ${styles.cellCustomer}`}>
                  <CaseCustomer item={item} />
                </span>
                <span className={`${styles.cell} ${styles.cellStage}`}>
                  <CaseStage item={item} />
                </span>
                <span className={`${styles.cell} ${styles.cellVolume}`}>
                  {/* An amount of an older Stand marks itself (`VolumeFigure`). */}
                  <CaseVolume item={item} missing="words" />
                </span>
                <span className={`${styles.cell} ${styles.cellWhen}`}>
                  <CaseWhen item={item} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
        {/* The register's foot, without a sum: this is a preview of five. */}
        <p className={styles.more}>
          <span>
            <Figures text={`${shown.length} von ${openCount} offenen`} />
          </span>
          <Link className="inline-link" to="/vorgaenge">
            Alle Vorgänge →
          </Link>
        </p>
      </div>
    );
  }

  return (
    <section className={styles.recent} aria-labelledby="recent-cases">
      <h2 id="recent-cases" className={`label ${styles.head}`}>
        {/* The mesh marker before a section title (design 2026-10-24 §8.6). */}
        <span className="marke" aria-hidden="true" />
        Zuletzt bearbeitet
      </h2>
      {content}
    </section>
  );
}
